import * as moment from 'moment';
import * as Statics from '@shared/core/statics';
import * as Consts from '@shared/core/consts';
import { Dates } from './dates.utils';
import { DateTimeRounder } from './date-time-rounder.utils';
import { LocationFutureOrdering } from './location-future-ordering.utils';
import { LocationOrderingTimeInfo } from './location-ordering-time-info.utils';
import { LocationOpenStatus } from './location-open-status.utils';
import { CollectionTypeGroupDetector } from './collection-type-group-detector';
import { Objects } from './objects.utils';
import { PickupsCreatorUtils } from './pickups-creator.utils';
import { OpeningHours } from './opening-hours.utils';
import { CollectionTypeHelper } from './collection-type-helper.utils';

export class Pickups {
    /**
     * Checks if provided date is range of future date range based on days diff form NOW
     * @param {Date} date to validate
     * @param {number} futureOrdersMinDay inital range day number counting from 0
     * @param {number} futureOrdersMaxDay end range day number
     * @returns {boolean}
     */
    public static isDateInFutureOrdersTimeRange(date: Date | string, futureOrdersMinDay: Nullable<number> = null, futureOrdersMaxDay: Nullable<number> = null): boolean {
        if (futureOrdersMinDay === null || futureOrdersMaxDay === null) return false;
        if (!date) return false;
        date = typeof date === 'string' ? Dates.createDate(date) : (date as Date);

        const diff: number = Dates.datesDiffInDays(new Date(), date);

        return diff >= futureOrdersMinDay && diff <= futureOrdersMaxDay;
    }

    /**
     * Checks if provided `pickupTime` object is valid future day for desired location
     *
     * @param {OLO.Ordering.PickupTime} pickupTime object
     * @param {OLO.DTO.LocationOrderingTimeInfoModel} timeInfo of specific location
     * @param {number} futureOrdersMinDay range start - difference in days relative to today's date
     * @param {number} futureOrdersMinDay range end - difference in days relative to today's date
     * @param futureOrdersMaxDay
     * @returns {boolean}
     */
    public static isFuturePickupTimeValid(
        pickupTime: OLO.Ordering.PickupTime,
        timeInfo: OLO.DTO.LocationOrderingTimeInfoModel,
        futureOrdersMinDay: Nullable<number> = null,
        futureOrdersMaxDay: Nullable<number> = null,
    ): boolean {
        /* Check if selected pickup time is in range of open +1 - close-1 hours */
        const startDate: Date = new Date(
            Dates.createDate(
                timeInfo.Date.split('T')
                    .map((obj, index) => (!index ? obj : timeInfo.OpeningTime))
                    .join('T'),
            ).getTime() +
                1 * 60 * 1000,
        );
        const endDate: Date = new DateTimeRounder(
            Dates.createDate(
                timeInfo.Date.split('T')
                    .map((obj, index) => (!index ? obj : timeInfo.ClosingTime))
                    .join('T'),
            ),
            'down',
        ).roundByHour();

        const targetDate = Dates.createDate(pickupTime?.DateLocalISO || Dates.getLocalISOFormatDate(Dates.createDate(null)));

        const isInLocationTimeRange: boolean = Pickups.isDateInFutureOrdersTimeRange(targetDate, futureOrdersMinDay, futureOrdersMaxDay);

        return targetDate >= startDate && targetDate <= endDate && isInLocationTimeRange === true;
    }

    /**
     * Based on YYYY-MM-DD, compares two dates
     *
     * @param {string} dateToCompare
     * @returns {Function} configured validator
     */
    public static datesMatchByDayCallback(dateToCompare: string): (obj: any) => boolean {
        return (obj) => Dates.getChunk(obj?.Date) === (Dates.getChunk(dateToCompare) || null);
    }

    /**
     * Based on location details, it returns information that helps validate some BL
     *
     * @param {OLO.DTO.OnlineOrderingLocationBusinessModel} location details
     * @returns {APICommon.LocationOrderingTimeInfoExtendedModel[]} calculated location ordering time objects
     */
    public static getFilteredOrderingTimeInfo(
        location: OLO.DTO.OnlineOrderingLocationBusinessModel,
        orderTypeId: Nullable<number>,
    ): APICommon.LocationOrderingTimeInfoExtendedModel[] {
        const { FutureOrderingMinDaysAhead: min, FutureOrderingMaxDaysAhead: max } = LocationFutureOrdering.getLocationFutureOrderingDetails({
            location,
            orderTypeId,
        });
        //
        //  Min = null or/and Max = null  -> No future ordering
        //  Min = 0 and Max = 0  -> Only today
        //  Min = 0 and Max >0  -> today and other days
        //  Min >= 1 and Max >=1  -> today not available and can order for future days only
        //

        if (!location) return [];

        const orderingTimeInfo = new LocationOrderingTimeInfo(location, orderTypeId).getOrderingTimeInfo();
        const config = new Statics.ConfigStatic().current;
        const collectionTypeConfig = new CollectionTypeHelper(config.collectionTypes).getDefaultCollectionType(orderTypeId);
        const orderType = location.OrderTypes?.find((obj) => collectionTypeConfig.orderTypeIds.includes(obj.Id));
        const availablePickupTimes = orderType?.AvailablePickupTimes?.Id ? orderType.AvailablePickupTimes : location.AvailablePickupTimes;

        if (!availablePickupTimes) return [];

        const filteredOrderingTimes = orderingTimeInfo?.reduce<OLO.DTO.LocationOrderingTimeInfoModel[]>((validTimes, timeInfo) => {
            const locationStatus = new LocationOpenStatus(timeInfo);
            if (!locationStatus.isOpen() || !Pickups.shouldIncludeTimeSlot(timeInfo, Dates.isToday(timeInfo.Date), availablePickupTimes, min, max)) {
                return validTimes;
            }

            return [...validTimes, timeInfo];
        }, []);

        if (!filteredOrderingTimes) return [];

        return filteredOrderingTimes.map((obj) => ({
            ...obj,
            IsOpen: new LocationOpenStatus(obj).isOpen(),
        }));
    }

    public static shouldIncludeTimeSlot(
        timeInfo: OLO.DTO.LocationOrderingTimeInfoModel,
        isTimeSlotToday: boolean,
        availablePickupTimes: APIv3_SNAPSHOT.AvailablePickupTimeSchemaModel,
        min: Nullable<number>,
        max: Nullable<number>,
    ): boolean {
        const isFutureOrderingAllowed = min !== null && max !== null;
        const isTodayAllowed = min === 0 || min === null;

        if (isTimeSlotToday) {
            if (!isTodayAllowed) {
                return false;
            }

            const lastPossibleSlotMinutes = OpeningHours.calculateLastPossibleSlot(
                Dates.timeToMinutes(timeInfo.ClosingTime),
                availablePickupTimes.NextTickInMinutes,
                availablePickupTimes.OrderingTimeEndOffsetInMinutes,
            );

            const now = new Date();
            const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

            if (currentTimeInMinutes > lastPossibleSlotMinutes) {
                return false;
            }

            return true;
        }

        return isFutureOrderingAllowed && Pickups.isDateInFutureOrdersTimeRange(timeInfo.Date, min, max);
    }

    /**
     * Creates base pickup information for location
     *
     * @param {number} locationNo
     * @param {number} asapPickupMins current minimum asap in minutes
     * @param {OLO.DTO.LocationOrderingTimeInfoModel[]} openingHours location opening hours
     * @param {number} orderTimeoutBufferMins determines how much time user has to create the order
     * @param {number} startBufferMins buffer in mins for initial pickup time
     * @param {Date} date for which calculations will be performed. Default is `new Date()`
     * @param {IStaticTexts} staticTexts translations of static texts
     * @returns {OLO.Ordering.PickupForLocation | null} base pickup information object. If null is returned, it indicate that today's ordering is unavailable,
     * but location might be avaliable in future orderings
     */
    public static calcInitialTimesObj({
        locationNo = null,
        asapPickupMins = null,
        minimumPreparationTime = null,
        orderTimeoutBufferMins = 0,
        endBufferMins = 0,
        startBufferMins = 0,
        openingHours = null,
        date = new Date(),
        delayAfterAsap = 0,
        nextTick = 0,
    }: Partial<OLO.Ordering.InitialPickupTimesParams>): OLO.Ordering.PickupForLocation {
        if (orderTimeoutBufferMins == null || startBufferMins == null) {
            throw new Error('OrderTimeoutBufferMins and startBufferMins must be provided');
        }

        const MILLISECONDS_PER_MINUTE = 60 * 1000;
        const localDate: string = date instanceof Date ? Dates.getLocalISOFormatDate(date) : date;
        const openingHourObj: OLO.DTO.LocationOrderingTimeInfoModel = openingHours?.find((day) => {
            const byDate = day.Date ? Dates.getChunk(day.Date) === Dates.getChunk(localDate) : false;
            const byDayOfWeek = day.DayOfWeek === date.getDay();

            return byDate || byDayOfWeek;
        });
        if (!openingHourObj) {
            return null;
        }

        const asapMinsFormDate = minimumPreparationTime != null ? minimumPreparationTime : asapPickupMins;
        const closingBuffer = orderTimeoutBufferMins + endBufferMins;
        const startTime = new Date(localDate.split('T')[0] + 'T' + openingHourObj.OpeningTime);
        const closeTime = new Date(localDate.split('T')[0] + 'T' + openingHourObj.ClosingTime);
        const now = new Date(date);
        const isOpen = now >= startTime && now <= closeTime;
        const locationWillBeOpenToday = now < startTime;

        let minimumPickupTime: Date;
        let minimumPickupTimeForAsap: Date;
        const maximumPickupTime = new Date(closeTime.getTime() - closingBuffer * MILLISECONDS_PER_MINUTE);

        if (isOpen) {
            minimumPickupTime = new Date(Math.max(now.getTime() + (nextTick > asapMinsFormDate ? nextTick : asapMinsFormDate) * MILLISECONDS_PER_MINUTE, startTime.getTime()));
            minimumPickupTime.setSeconds(0, 0);
            minimumPickupTimeForAsap = new Date(now.getTime());
            minimumPickupTimeForAsap.setSeconds(0, 0);
        } else if (locationWillBeOpenToday) {
            minimumPickupTime = new Date(startTime.getTime() + startBufferMins * MILLISECONDS_PER_MINUTE);
            minimumPickupTime.setSeconds(0, 0);
            minimumPickupTimeForAsap = new Date(startTime.getTime() + startBufferMins * MILLISECONDS_PER_MINUTE);
            minimumPickupTimeForAsap.setSeconds(0, 0);
        }

        if (!minimumPickupTime || !minimumPickupTimeForAsap) {
            return null;
        }

        if (minimumPickupTime > maximumPickupTime) {
            return null;
        }

        minimumPickupTime.setSeconds(0, 0);
        minimumPickupTimeForAsap.setSeconds(0, 0);

        return {
            DayOfWeek: openingHourObj.DayOfWeek,
            DayName: Dates.getDayName(openingHourObj.DayOfWeek),
            LocationNo: locationNo,
            Date: localDate,
            IsOpen: isOpen,
            OpeningTime: startTime,
            ClosingTime: closeTime,
            MinimumPickupTimeForAsap: minimumPickupTimeForAsap,
            MinimumPickupTime: minimumPickupTime,
            MaximumPickupTime: maximumPickupTime,
        };
    }

    /**
     * Helper that extracts location's time info object based on the given date
     *
     * @param {Date} date
     * @param {OLO.DTO.LocationOrderingTimeInfoModel[]} openingHours
     * @returns {OLO.DTO.LocationOrderingTimeInfoModel | null} extracted object
     */
    public static findLocationOrderingTimeInfoObject(date: Date, openingHours: OLO.DTO.LocationOrderingTimeInfoModel[]): Nullable<OLO.DTO.LocationOrderingTimeInfoModel> {
        const localDate: string = Dates.getLocalISOFormatDate(date);

        return (
            openingHours?.find((day) => {
                const byDate = day.Date ? Dates.getChunk(day.Date) === Dates.getChunk(localDate) : false;
                const byDayOfWeek = day.DayOfWeek === date.getDay();

                return byDate || byDayOfWeek;
            }) || null
        );
    }

    /**
     * Helper that extracts Pickup Time info model with MinimumPickupTime
     *
     * @param {Date} date
     * @param {OLO.DTO.LocationOrderingTimeInfoModel[]} openingHours
     * @returns {Nullable<OLO.DTO.LocationOrderingPickupTimeInfoModel>} extracted object
     */
    public static findByDateLocationPickupTimeObject(
        date: Date,
        orderingObj: Nullable<OLO.DTO.LocationOrderingTimeInfoModel>,
    ): Nullable<OLO.DTO.LocationOrderingPickupTimeInfoModel> {
        if (!orderingObj) {
            return null;
        }

        return orderingObj.PickupTimes?.find((obj) => Dates.isHourInHoursRange(date, obj.From, obj.To, 'both'));
    }

    public static isFutureOrderingOnly(opts: OLO.Ordering.GeneratePickupsParams, minDays: number, maxDays: number): boolean {
        return opts.schedule === true && opts.location !== null && maxDays > 0 && minDays > 0 && minDays <= maxDays;
    }

    /**
     * Base object that helps build future orders lists or validate data
     *
     * @param {OLO.DTO.LocationOrderingTimeInfoModel} obj location's time info object
     * @param {string} format date format. Default ```DATE_DISPLAY_FORMAT```
     * @param {boolean} prefixes add ```Today``` or ```Tomorrow``` prefixes to dates
     * @returns {OLO.Ordering.Period}
     */
    public static createPeriodObject(obj: OLO.DTO.LocationOrderingTimeInfoModel, format: string = Consts.DATE_DISPLAY_FORMAT, prefixes: boolean = true): OLO.Ordering.Period {
        const text = new Statics.TextsStatic().current;
        const config = new Statics.ConfigStatic().current;
        const locale = config.localization?.locale || 'en-AU';
        const normalizeDate = (date: string) =>
            date
                .split('T')
                .map((key, index) => (index === 0 ? key : obj?.ClosingTime ? obj.ClosingTime + '.000' : '12:00:00.000'))
                .join('T');
        const now: Date = new Date();
        const newDate: string = normalizeDate(obj.Date);
        const Id = Dates.datesDiffInDays(now, newDate);

        let Name: string = '';
        if (prefixes && Id < 2) {
            if (Id === 0) {
                Name = `${text.pickup.today}, `;
            } else {
                Name = `${text.pickup.tomorrow}, `;
            }
        }

        Name += moment(Dates.createDate(newDate)).locale(locale).format(format);

        return {
            Id: Id + 1,
            DayName: Dates.getDayName(obj.DayOfWeek),
            DayOfWeek: obj.DayOfWeek,
            Date: newDate,
            IsToday: Id === 0,
            Name,
        };
    }

    /**
     * Creates pickups list for future orders. Similar to [[Pickups.generatePickupTimesList]] method
     *
     * @param {OLO.Ordering.Period} period object generated by [[Pickups.createPeriodObject]] method
     * @param {OLO.Ordering.GeneratePickupsParams} params
     * @returns {OLO.Ordering.PickupTime[]} future orders pickups list
     */
    public static generateTimeSlotsList(params: OLO.Ordering.GeneratePickupsParams): OLO.Ordering.PickupTime[] {
        const opts: OLO.Ordering.GeneratePickupsParams = {
            location: null,
            openingHours: null,
            orderTimeoutBufferMins: 60,
            startBufferMins: 60,
            endBufferMins: 0,
            nextTick: 60,
            displayFormat: Consts.DATE_DISPLAY_FORMAT,
            orderTypeId: null,
            ...Objects.filterObject(params),
        };

        const result: OLO.Ordering.PickupTime[] = [];

        if (!opts.openingHours?.length) return result;

        const filteredOpeningHours = opts.schedule ? opts.openingHours : opts.openingHours.filter((oh) => Dates.isToday(new Date(oh.Date)));

        if (!filteredOpeningHours.length) return result;

        const sortedOpeningHours = OpeningHours.sort(filteredOpeningHours);
        const now = new Date();
        const config = new Statics.ConfigStatic().current;
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        for (const openingHour of sortedOpeningHours) {
            const rangeInfo = OpeningHours.getTimeRangeInfo(openingHour, opts.openingHours);
            const baseDate = new Date(openingHour.Date);
            const openingMinutes: number = Dates.timeToMinutes(openingHour.OpeningTime);
            const closingMinutes: number = Dates.timeToMinutes(openingHour.ClosingTime);
            const effectiveStartBuffer = rangeInfo.isFirst ? opts.startBufferMins : 0;
            const effectiveEndBuffer = rangeInfo.isLast ? opts.endBufferMins : 0;
            const lastPossibleSlotMinutes = OpeningHours.calculateLastPossibleSlot(closingMinutes, opts.nextTick, effectiveEndBuffer);
            const isToday = Dates.isToday(openingHour.Date);
            const openingPlusBuffer = openingMinutes + effectiveStartBuffer;

            let currentSlotMinutes;

            if (!isToday) {
                currentSlotMinutes = openingMinutes + effectiveStartBuffer;
            } else if (currentMinutes >= lastPossibleSlotMinutes) {
                continue;
            } else if (currentMinutes < openingMinutes) {
                currentSlotMinutes = openingPlusBuffer;
            } else if (currentMinutes < openingPlusBuffer) {
                const timeWithDelay = currentMinutes + (opts.delayAfterAsap || 0);
                currentSlotMinutes = timeWithDelay <= openingPlusBuffer ? openingPlusBuffer : timeWithDelay;
            } else {
                currentSlotMinutes = currentMinutes + (opts.minimumPreparationTime ?? 0);
                if (opts.delayAfterAsap) {
                    currentSlotMinutes += opts.delayAfterAsap;
                }
            }

            currentSlotMinutes = Pickups._roundToNearestTick(currentSlotMinutes, opts.nextTick);

            if (currentSlotMinutes > lastPossibleSlotMinutes) {
                continue;
            }

            const collectionTypeAllowedPickupTimeASAP = new CollectionTypeGroupDetector(opts.orderTypeId, config).collectionTypeAllowedPickupTimeASAP();

            if (collectionTypeAllowedPickupTimeASAP && !opts.displayAsTimespans && currentMinutes >= openingMinutes && currentMinutes <= lastPossibleSlotMinutes && isToday) {
                const asapDate = new Date(now);
                const asapSlot = PickupsCreatorUtils.createPickupTimeObj({
                    isSchedule: opts.schedule,
                    forDate: asapDate,
                    orderTypeId: opts.orderTypeId,
                    edgeDate: new Date(asapDate.getTime() + opts.orderTimeoutBufferMins * 60 * 1000),
                    index: result.length + 1,
                    isAsap: true,
                    nextTick: opts.nextTick,
                    isDisplayAsTimespans: opts.displayAsTimespans,
                });

                result.push(asapSlot);
            }

            while (currentSlotMinutes <= lastPossibleSlotMinutes) {
                const localDate = new Date(baseDate);
                localDate.setHours(Math.floor(currentSlotMinutes / 60), currentSlotMinutes % 60, 0, 0);

                const utcDate = new Date(localDate.getTime());
                const slot = PickupsCreatorUtils.createPickupTimeObj({
                    isSchedule: opts.schedule,
                    forDate: utcDate,
                    orderTypeId: opts.orderTypeId,
                    edgeDate: null,
                    index: result.length + 1,
                    isAsap: false,
                    nextTick: opts.nextTick,
                    isDisplayAsTimespans: opts.displayAsTimespans,
                });

                result.push(slot);
                currentSlotMinutes += opts.nextTick;
            }
        }

        return result;
    }

    private static _roundToNearestTick(time: number, tick: number): number {
        if (time === undefined) {
            throw new Error('Time slot is undefined');
        }

        const remainder = time % tick;

        return time + (remainder > 0 ? tick - remainder : 0);
    }
}
